<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash/cloneDeep';
import { useItemConfigurationStore } from '@/store/ItemConfigurationStore';
import { usePromoComposable } from '@/components/admin/usePromoComposable';
import ProgressSpinner from 'primevue/progressspinner';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import TableColumn from 'primevue/column';
import UpdateAgencyBlacklistModal from '@/components/modal/UpdateAgencyBlacklistModal.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const { canEditAdminSetup } = usePromoComposable();
const configurationModule = useItemConfigurationStore();
const filters = ref();
const loading = ref(false);
const dialog = useDialog();
const agencyBlacklist = ref<any>([]);

const initBlaclist = () => {
  return {
    email: '',
    phone: ''
  };
};

const getBlacklistData = () => {
  loading.value = true;
  configurationModule
    .getListAgency()
    .then((res: any) => {
      agencyBlacklist.value = res;
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    })
    .finally(() => (loading.value = false));
};
getBlacklistData();

const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    phone: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initFilters();

const clearFilter = () => {
  initFilters();
};

const deleteAgency = (data?: any) => {
  configurationModule
    .deleteAgency(data.sk)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.delete_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      getBlacklistData();
    });
};

const updateAgency = (data?: any) => {
  const dialogRef = dialog.open(UpdateAgencyBlacklistModal, {
    props: {
      header: data ? t('gqt_admin_setup.agency.agency_update_title') : t('gqt_admin_setup.agency.agency_add_title'),
      modal: true,
      maximizable: true
    },
    data: {
      detailBlacklist: cloneDeep(data) || initBlaclist()
    },
    emits: {
      closeModal: () => {
        dialogRef.close();
        getBlacklistData();
      }
    }
  });
};
</script>

<template>
  <LoadingSpinner v-if="loading" />
  <div class="card agency-blacklist">
    <div class="font-bold mb-3 text-xl uppercase font-primary-color">{{ $t('gqt_admin_setup.agency_blacklist') }}</div>
    <DataTable v-model:filters="filters" :value="agencyBlacklist" paginator showGridlines :rows="10" dataKey="id" filterDisplay="menu" :globalFilterFields="['email', 'phone']">
      <template #header>
        <div class="flex justify-content-between flex-col sm:flex-row">
          <div class="flex justify-content-left flex-col sm:flex-row gap-3">
            <Button id="agency-clear-btn" type="button" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="p-button-secondary mb-2" @click="clearFilter()" />
          </div>
          <div class="justify-content-right">
            <span class="p-input-icon-left mb-2">
              <i class="pi pi-search"></i>
              <InputText id="agency-search" v-model="filters['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
            </span>
          </div>
        </div>
      </template>
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <template #loading>
        <ProgressSpinner class="w-4rem h-4rem" strokeWidth="8" animationDuration="2s" />
      </template>
      <TableColumn field="phone" :header="$t('gqt_admin_setup.agency.agency_phone')" class="break-word" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <span class="long-text">
            {{ data.phone }}
          </span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('gqt_admin_setup.agency.agency_phone')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn field="email" :header="$t('gqt_admin_setup.agency.agency_email')" class="break-word" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <span class="long-text">
            {{ data.email }}
          </span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('gqt_admin_setup.agency.agency_email')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn :header="$t('common.action')" v-if="canEditAdminSetup">
        <template #body="{ data }">
          <div class="flex">
            <Button icon="pi pi-pencil" class="p-button-text" @click="updateAgency(data)" />
            <Button icon="pi pi-trash" class="p-button-text" @click="deleteAgency(data)" />
          </div>
        </template>
      </TableColumn>
    </DataTable>
    <div>
      <Button id="agency-add-btn" :disabled="!canEditAdminSetup" :label="$t('gqt_admin_setup.agency.agency_travel_agent_add')" icon="pi pi-plus" class="p-button-text" @click="updateAgency()" />
    </div>
  </div>
</template>
